import { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { formatPrice } from '@/lib/pricing';
import { 
  CheckCircle2, 
  Home, 
  Mail, 
  Phone, 
  MapPin, 
  Package,
  CreditCard,
  Smartphone,
  Banknote,
  Store,
  Truck,
  AlertTriangle,
  Info
} from 'lucide-react';

const paymentIcons = {
  credit_card: { icon: CreditCard, label: '信用卡' },
  linepay: { icon: Smartphone, label: 'LINE Pay' },
  atm: { icon: Banknote, label: 'ATM 轉帳' },
  cvs: { icon: Store, label: '超商代碼' },
  cod: { icon: Truck, label: '貨到付款' },
};

export function OrderSuccessPage() {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    // 滾動到頂部
    window.scrollTo(0, 0);

    // 從 state 或 localStorage 取得訂單
    if (location.state?.order) {
      setOrder(location.state.order);
    } else {
      // 從 localStorage 載入訂單
      const orders = JSON.parse(localStorage.getItem('nmn_orders') || '[]');
      const foundOrder = orders.find(o => o.order_id === orderId);
      if (foundOrder) {
        setOrder(foundOrder);
      } else {
        // 找不到訂單，導回首頁
        setTimeout(() => navigate('/'), 3000);
      }
    }
  }, [orderId, location.state, navigate]);

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">找不到訂單</h2>
            <p className="text-gray-600 mb-4">正在導回首頁...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const PaymentIcon = paymentIcons[order.payment_method]?.icon || CreditCard;
  const paymentLabel = paymentIcons[order.payment_method]?.label || order.payment_method;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* 成功訊息 */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-4">
            <CheckCircle2 className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            訂單成立成功！
          </h1>
          <p className="text-lg text-gray-600">
            感謝您的訂購，我們已收到您的訂單
          </p>
        </div>

        {/* 訂單編號 */}
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-blue-700 mb-1">訂單編號</p>
              <p className="text-2xl font-bold text-blue-900 font-mono tracking-wider">
                {order.order_id}
              </p>
              <p className="text-xs text-blue-600 mt-2">
                請保存此訂單編號以便查詢訂單狀態
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 訂單資訊 */}
        <div className="space-y-6">
          {/* 商品資訊 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                商品資訊
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {order.items.map((item) => (
                  <div key={item.id} className="flex gap-4">
                    <div className="w-20 h-20 rounded-md overflow-hidden bg-gray-100 flex-shrink-0">
                      <img
                        src="/placeholder.jpg"
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{item.shortName}</p>
                      <p className="text-sm text-gray-500">SKU: {item.sku}</p>
                      <p className="text-sm text-gray-600">
                        {formatPrice(item.price)} × {item.quantity}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{formatPrice(item.subtotal)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* 收件資訊 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                收件資訊
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <span className="text-gray-600 w-20 flex-shrink-0">收件人</span>
                  <span className="font-medium">{order.customer.name}</span>
                </div>
                <div className="flex items-start gap-2">
                  <Phone className="h-4 w-4 text-gray-400 mt-0.5" />
                  <span className="font-medium">{order.customer.phone}</span>
                </div>
                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 text-gray-400 mt-0.5" />
                  <span className="font-medium">{order.customer.email}</span>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                  <span className="font-medium">{order.customer.address}</span>
                </div>
                {order.notes && (
                  <div className="flex items-start gap-2 pt-2 border-t">
                    <span className="text-gray-600 w-20 flex-shrink-0">備註</span>
                    <span className="text-gray-700">{order.notes}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* 付款資訊 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PaymentIcon className="h-5 w-5" />
                付款資訊
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">付款方式</span>
                  <Badge variant="secondary">{paymentLabel}</Badge>
                </div>

                <Separator />

                {/* 價格明細 */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">小計</span>
                    <span>{formatPrice(order.pricing.subtotal)}</span>
                  </div>

                  {order.pricing.bulk_discount_amount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>團購優惠</span>
                      <span>-{formatPrice(order.pricing.bulk_discount_amount)}</span>
                    </div>
                  )}

                  {order.pricing.discount_code_amount > 0 && (
                    <div className="flex justify-between text-sm text-blue-600">
                      <span>折扣碼（{order.pricing.discount_code}）</span>
                      <span>-{formatPrice(order.pricing.discount_code_amount)}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">運費</span>
                    <span className={order.pricing.shipping === 0 ? 'text-green-600' : ''}>
                      {order.pricing.shipping === 0 ? '免運費' : formatPrice(order.pricing.shipping)}
                    </span>
                  </div>

                  <Separator />

                  <div className="flex justify-between items-baseline">
                    <span className="text-lg font-semibold">訂單總額</span>
                    <span className="text-2xl font-bold text-blue-600">
                      {formatPrice(order.pricing.total)}
                    </span>
                  </div>

                  {order.pricing.total_savings > 0 && (
                    <div className="text-sm text-green-600 text-right">
                      已為您節省 {formatPrice(order.pricing.total_savings)}
                    </div>
                  )}
                </div>

                {/* 付款說明 */}
                <Alert className="bg-yellow-50 border-yellow-200">
                  <Info className="h-4 w-4 text-yellow-600" />
                  <AlertTitle className="text-yellow-900">付款說明</AlertTitle>
                  <AlertDescription className="text-yellow-800 text-sm">
                    {order.payment_method === 'credit_card' && 
                      '我們會透過 Email 發送信用卡付款連結給您。'}
                    {order.payment_method === 'linepay' && 
                      '我們會透過 Email 發送 LINE Pay 付款連結給您。'}
                    {order.payment_method === 'atm' && 
                      '我們會透過 Email 發送虛擬帳號資訊給您，請於 3 天內完成轉帳。'}
                    {order.payment_method === 'cvs' && 
                      '我們會透過 Email 發送繳費代碼給您，可至便利商店繳費。'}
                    {order.payment_method === 'cod' && 
                      '送達時請準備現金或信用卡付款給物流人員。'}
                  </AlertDescription>
                </Alert>
              </div>
            </CardContent>
          </Card>

          {/* 後續步驟 */}
          <Card>
            <CardHeader>
              <CardTitle>後續步驟</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-semibold">1</span>
                  </div>
                  <div>
                    <p className="font-medium">確認訂單</p>
                    <p className="text-sm text-gray-600">
                      我們會透過 Email 發送訂單確認信給您
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-semibold">2</span>
                  </div>
                  <div>
                    <p className="font-medium">完成付款</p>
                    <p className="text-sm text-gray-600">
                      請依照付款方式說明完成付款
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-semibold">3</span>
                  </div>
                  <div>
                    <p className="font-medium">準備出貨</p>
                    <p className="text-sm text-gray-600">
                      確認收到款項後，我們會在 1-3 個工作天內為您出貨
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 font-semibold">4</span>
                  </div>
                  <div>
                    <p className="font-medium">配送到府</p>
                    <p className="text-sm text-gray-600">
                      出貨後會透過 Email 和簡訊通知您物流追蹤號碼
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 客服資訊 */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>需要協助？</AlertTitle>
            <AlertDescription>
              如有任何問題，歡迎透過以下方式聯繫我們：
              <div className="mt-2 space-y-1">
                <p className="flex items-center gap-2">
                  <Mail className="h-3 w-3" />
                  Email: support@nmn.wedopr.com
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="h-3 w-3" />
                  客服專線: 02-77307865
                </p>
              </div>
            </AlertDescription>
          </Alert>
        </div>

        {/* 操作按鈕 */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="min-w-[200px]"
          >
            <Home className="mr-2 h-4 w-4" />
            返回首頁
          </Button>
          <Button
            onClick={() => navigate('/product')}
            className="min-w-[200px]"
          >
            繼續購物
          </Button>
        </div>

        {/* 詐騙提醒 */}
        <Alert variant="destructive" className="mt-8">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>防詐騙提醒</AlertTitle>
          <AlertDescription>
            本店絕不會以「交易失敗」或「付款錯誤」為由，要求您至提款機操作任何取消交易或轉帳動作。
            若接獲可疑通知，請立即與官方客服聯繫查證。
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
}
